import { StatusBadge } from '@vrplatform/components';
import { useDashboard } from '@vrplatform/data';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { ensure, hasValue } from '@vrplatform/utils';
import { Group } from '@mantine/core';
import { useMemo } from 'react';
import type { ReservationRow } from './useReservationsTableQuery';

type Props = {
  hideTotal: boolean;
};

export const useReservationsTableColumns = ({ hideTotal }: Props) => {
  const [dashboard] = useDashboard();
  const isOwnerPortal = dashboard === 'owner';

  return useMemo(
    () =>
      ensure<(MRT_ColumnDef<ReservationRow> | undefined)[]>([
        {
          accessorKey: 'guestName',
          header: 'Guest',
          type: 'string',
        },
        {
          accessorKey: 'confirmationCode',
          header: 'Confirmation Code',
          type: 'string',
        },
        {
          accessorKey: 'bookedAt',
          header: 'Booked at',
          type: 'date',
        },
        {
          accessorFn: (row) => ({
            end: row.checkOut,
            start: row.checkIn,
          }),
          header: 'Dates',
          id: 'dates',
          type: 'dateRange',
        },
        {
          accessorKey: 'nights',
          header: 'Nights',
          mantineTableBodyCellProps: { align: 'right' },
          mantineTableHeadCellProps: { align: 'right' },
          maxSize: 80,
          minSize: 80,
          size: 80,
          type: 'number',
        },
        {
          accessorKey: 'status',
          header: 'Status',
          Cell: ({ row }) => {
            const data = row.original;

            if (
              data.issues?.find(
                (issue) => issue.code === 'unassignedAccount'
              ) &&
              !isOwnerPortal
            ) {
              return (
                <StatusBadge
                  label="Unmapped financials"
                  status="error"
                  tooltip="Reservation includes unmapped financial lines"
                />
              );
            }

            const lifecycleStatus =
              data.status === 'booked'
                ? { label: 'Booked', status: 'success' as const }
                : data.status === 'canceled'
                  ? { label: 'Canceled', status: 'cancelled' as const }
                  : data.status === 'inactive'
                    ? { label: 'Deleted', status: 'archived' as const }
                    : data.status === 'blocked'
                      ? { label: 'Blocked', status: 'blocked' as const }
                      : data.status === 'inquiry'
                        ? { label: 'Inquiry', status: 'pending' as const }
                        : data.status === 'on_hold'
                          ? { label: 'On hold', status: 'pending' as const }
                          : undefined;

            return (
              <Group gap="xs" wrap="nowrap">
                {lifecycleStatus ? (
                  <StatusBadge
                    label={lifecycleStatus.label}
                    status={lifecycleStatus.status}
                  />
                ) : (
                  <StatusBadge
                    label="Unsupported status"
                    status="error"
                    tooltip={`Unsupported reservation lifecycle status: ${String(data.status)}`}
                  />
                )}
                {data.generalLedgerStatus === 'inactive' ? (
                  <StatusBadge label="Archived" status="archived" />
                ) : null}
              </Group>
            );
          },
        },
        {
          accessorFn: (row) => row.listing?.name ?? null,
          header: 'Listing',
          id: 'listing',
          type: 'listing',
        },
        isOwnerPortal
          ? undefined
          : {
              accessorFn: (row) => {
                if (row.payment?.status === 'coHost') return null;

                return (
                  (row.payment?.expected ?? 0) - (row.payment?.received ?? 0)
                );
              },
              header: 'Unpaid',
              id: 'unpaid',
              mantineTableBodyCellProps: { align: 'right' },
              mantineTableHeadCellProps: { align: 'right' },
              type: 'accounting',
              typeProps: ({ row }) => ({
                currency: row.currency || undefined,
              }),
            },
        hideTotal
          ? undefined
          : {
              accessorFn: (row) => row.payment?.expected ?? null,
              header: 'Total',
              id: 'total',
              mantineTableBodyCellProps: { align: 'right' },
              mantineTableHeadCellProps: { align: 'right' },
              type: 'accounting',
              typeProps: ({ row }) => ({
                currency: row.currency || undefined,
              }),
            },
        isOwnerPortal
          ? undefined
          : {
              accessorFn: (row) => row.managerCentTotal ?? null,
              header: 'PM Revenue',
              id: 'pmRevenue',
              mantineTableBodyCellProps: { align: 'right' },
              mantineTableHeadCellProps: { align: 'right' },
              type: 'accounting',
              typeProps: ({ row }) => ({
                currency: row.currency || undefined,
              }),
            },
        isOwnerPortal
          ? undefined
          : {
              accessorFn: (row) => row.taxCentTotal ?? null,
              header: 'Taxes',
              id: 'taxes',
              mantineTableBodyCellProps: { align: 'right' },
              mantineTableHeadCellProps: { align: 'right' },
              type: 'accounting',
              typeProps: ({ row }) => ({
                currency: row.currency || undefined,
              }),
            },
        isOwnerPortal
          ? undefined
          : {
              accessorFn: (row) => row.ownersCentTotal ?? null,
              header: 'Owner Revenue',
              id: 'ownerRevenue',
              mantineTableBodyCellProps: { align: 'right' },
              mantineTableHeadCellProps: { align: 'right' },
              type: 'accounting',
              typeProps: ({ row }) => ({
                currency: row.currency || undefined,
              }),
            },
      ]).filter(hasValue),
    [hideTotal, isOwnerPortal]
  );
};
